// Build-time fallback only — prefer subscriptionsEnabled(user) below, which
// reads the value the server actually resolved.
export const SUBSCRIPTIONS_ENABLED =
  import.meta.env.REACT_APP_SUBSCRIPTIONS_ENABLED === 'true';

/**
 * Whether paid-tier enforcement is on, as resolved by the SERVER.
 *
 * The build-time constant above is baked in at deploy, so flipping
 * SUBSCRIPTIONS_ENABLED in the ops console used to move the backend Pro gates
 * and the minutes quota while leaving the UI exactly as it shipped. `/auth/me`
 * now echoes the resolved flag, so the paywall surfaces and the gates behind
 * them turn on together.
 *
 * Falls back to the build-time constant when the field is absent — a user
 * object from a backend that predates `flags`, or a render before /auth/me has
 * landed. That keeps the old behaviour rather than guessing.
 */
export function subscriptionsEnabled(user) {
  const resolved = user?.flags?.subscriptions_enabled;
  return typeof resolved === 'boolean' ? resolved : SUBSCRIPTIONS_ENABLED;
}

/**
 * Whether instructors may delete a student, as resolved by the SERVER.
 *
 * Same `/auth/me` `flags` mechanism as subscriptionsEnabled, with one
 * difference: there is no build-time constant to fall back to, because this
 * flag guards an ability that shipped long before it. So an absent field
 * defaults to ENABLED — a user object from a backend that predates the flag,
 * or a render before /auth/me lands, must not hide a working button. Worst
 * case is a button that earns a 403; failing closed would take deletion away
 * from everyone on a stale payload.
 *
 * The server gate (DELETE /students/{id} → 403 student_delete_disabled)
 * remains authoritative — this only avoids offering a destructive confirm
 * that can't succeed.
 */
export function studentDeleteEnabled(user) {
  return user?.flags?.student_delete_enabled !== false;
}

/**
 * Whether instructors may mark an occurrence as a no-show, per the SERVER.
 *
 * Same mechanism and same default-enabled fallback as studentDeleteEnabled
 * above: no build-time constant exists, because the action shipped long before
 * the flag, so an absent field must read as ENABLED rather than hide a working
 * button on a stale `/auth/me` payload.
 *
 * The server gate (POST .../no-show → 403 no_show_disabled) stays
 * authoritative; this only avoids offering an action that can't succeed.
 */
export function noShowEnabled(user) {
  return user?.flags?.no_show_enabled !== false;
}

/**
 * Whether the practice-rehearsal (recording) feature is on, per the SERVER.
 *
 * A runtime kill switch (defaults ON) with the same default-enabled fallback as
 * the two helpers above — no build-time constant exists, because rehearsals
 * shipped long before the flag, so an absent field must read as ENABLED rather
 * than hide a live feature on a stale payload.
 *
 * Accepts EITHER shape: the instructor `user` carries it under
 * `flags.rehearsals_enabled` (from /auth/me), while the student-portal account
 * object carries it top-level as `rehearsals_enabled` (from /student/auth/me) —
 * the portal has no `flags` sub-object. The server 404 on the recording
 * endpoints stays authoritative; this only avoids offering a CTA that can't
 * succeed.
 */
export function rehearsalsEnabled(source) {
  const resolved = source?.flags?.rehearsals_enabled ?? source?.rehearsals_enabled;
  return resolved !== false;
}

/**
 * Whether manual practice-time logging (and everything read off it) is on, per
 * the SERVER.
 *
 * A runtime kill switch (defaults ON) with the same absent-means-enabled
 * fallback as the helpers above — manual logging shipped long before the flag,
 * so a stale payload must not hide a live feature.
 *
 * Accepts EITHER shape, like `rehearsalsEnabled`: the instructor `user` carries
 * it under `flags.practice_logs_enabled` (from /auth/me), the student-portal
 * account top-level as `practice_logs_enabled` (from /student/auth/me).
 *
 * Deliberately SEPARATE from `rehearsalsEnabled`: a rehearsal recording also
 * counts as a practice day, so turning manual logging off must not take a
 * student's streak away while they are still recording.
 */
export function practiceLogsEnabled(source) {
  const resolved = source?.flags?.practice_logs_enabled ?? source?.practice_logs_enabled;
  return resolved !== false;
}

/**
 * Whether the metronome/tuner panel shows INSIDE the recording screen, per the
 * SERVER.
 *
 * A launch-with-a-kill-switch (defaults ON), read with the same absent-means-
 * enabled fallback as the helpers above: there is no build-time constant, so a
 * payload that predates the field must ship the panel rather than hide it.
 *
 * Unlike the other flags this one has no server gate behind it, and doesn't
 * need one — the panel is pure client UI over tools that are already reachable
 * at /tools, so the worst case of a stale `true` is a surface we wanted to
 * retire lingering for one page load, not an action that 403s.
 */
export function recordingToolsEnabled(user) {
  return user?.flags?.recording_tools_enabled !== false;
}

/**
 * Whether instructors may attach files to a lesson, per the SERVER.
 *
 * Same `/auth/me` `flags` mechanism as the three helpers above, but the
 * fallback is INVERTED: this one defaults to DISABLED when the field is
 * absent. Those default open because they guard abilities that shipped long
 * before their flags, so a stale payload must not hide a working button. This
 * guards a brand-new ability instead — no client predates it, so an absent
 * field means "backend without the feature", and offering an upload affordance
 * that can only 404 would be worse than offering nothing.
 *
 * The server gate (the attachment endpoints 404) stays authoritative.
 */
export function lessonAttachmentsEnabled(user) {
  return user?.flags?.lesson_attachments_enabled === true;
}

/**
 * Whether the Opportunities tab on the Programs page is on, per the SERVER.
 *
 * A runtime kill switch (defaults ON), read with the same absent-means-enabled
 * fallback as the helpers above and for the same reason: the tab shipped long
 * before the flag, so a payload that predates the field must not hide a live
 * surface.
 *
 * The server 404 on the browse/detail/apply endpoints stays authoritative;
 * this hides the tab and, just as importantly, skips a fetch that could only
 * 404 — the Programs page loads all three tabs at once, so a request left in
 * would take the whole page down with it.
 */
export function programOpportunitiesEnabled(user) {
  return user?.flags?.program_opportunities_enabled !== false;
}

/**
 * Whether the Invites tab on the Programs page is on, per the SERVER.
 *
 * Same kill-switch mechanism and same absent-means-enabled fallback as
 * `programOpportunitiesEnabled`. The nav dot that counts pending invitations
 * needs no separate check here: the server zeroes `pending_program_invites`
 * when this is off, so the count and the tab it points at can never disagree.
 */
export function programInvitesEnabled(user) {
  return user?.flags?.program_invites_enabled !== false;
}

/**
 * Whether the instructor's Invoice history (the sidebar tab, its page and the
 * list on each student's page) is on, per the SERVER.
 *
 * Tuition invoicing is retired, so this is the retained history only, and it
 * defaults CLOSED: an absent flag reads as off, like
 * `lessonAttachmentsEnabled`. It narrows the build-time `BILLING_ENABLED`
 * below rather than replacing it, so callers check both. The history
 * endpoints still answer either way, and the student portal's own list is
 * unaffected.
 */
export function invoiceHistoryEnabled(user) {
  return user?.flags?.invoice_history_enabled === true;
}

export const STUDENT_PORTAL_ENABLED =
  import.meta.env.REACT_APP_STUDENT_PORTAL_ENABLED !== 'false';

// Lesson scheduling (Spec 04 Phase 1) — off by default until the QA bake.
export const SCHEDULING_ENABLED =
  import.meta.env.REACT_APP_SCHEDULING_ENABLED === 'true';

export const SCHEDULER_VIEW_ENABLED =
  import.meta.env.REACT_APP_SCHEDULER_VIEW_ENABLED === 'true';

// Instructor→student billing via Stripe Connect (Spec 05) — off by default.
// In prod this flips together with SUBSCRIPTIONS_ENABLED (billing is Pro-only).
export const BILLING_ENABLED =
  import.meta.env.REACT_APP_BILLING_ENABLED === 'true';

// Program listings (org marketplace) — off by default. Gates the instructor
// Programs page (/instructors/:userId/programs) and the public /programs
// directory; the org portal's own Programs page stays available (portal access
// is already gated).
export const MARKETPLACE_ENABLED =
  import.meta.env.REACT_APP_MARKETPLACE_ENABLED === 'true';

// Practice assignments (Spec 01) — off by default until the full loop ships
// on all three surfaces.
export const ASSIGNMENTS_ENABLED =
  import.meta.env.REACT_APP_ASSIGNMENTS_ENABLED === 'true';

/**
 * Goals (Spec 07 / F6) — what the practising is for.
 *
 * A LAUNCH switch, so it defaults CLOSED like LESSON_ATTACHMENTS_ENABLED and
 * unlike the kill switches: nothing shipped before it, and an unset variable
 * must not turn it on. The server's GOALS_ENABLED 404s independently.
 */
export const GOALS_ENABLED = import.meta.env.REACT_APP_GOALS_ENABLED === 'true';

// Public pricing link — temporarily hidden from the marketing nav + footer
// (July 2026). The /pricing ROUTE stays live and PricingPage is untouched, so
// links already in the wild and the Settings upgrade path still resolve; this
// only removes the ways a visitor can navigate there from the site chrome.
// Flip back to `true` to restore both entries. Deliberately a plain constant
// rather than an env var: it's a short-lived editorial call that should be the
// same in every environment, not per-environment configuration.
export const PRICING_LINK_VISIBLE = false;

// Whether to show actual subscription prices, or a "coming soon" placeholder.
//
// ON as of the pricing restructure going live: the copy was finished by X9 and
// the four instructor prices — Studio monthly/annual and Studio AI (internal
// key `concertmaster`) monthly/annual — now exist in Stripe, so the tiles and
// the Upgrade button beside them point at something real. That was the only
// thing holding this down; it was never about the copy.
//
// Covers the four web surfaces that quote a price: PricingPage (public),
// StripeCheckout (the paywall itself), the SettingsPage plan grid, and
// TopUpCheckout. Note the last of those needs STRIPE_PRICE_ID_TOPUP_600 as
// well — a top-up is its own price, not part of the subscription ladder, and
// the endpoint 503s until it is configured.
//
// It does NOT cover org (O3) or family (F2) pricing. Those checkouts live on
// their own pages, are gated by SUBSCRIPTIONS_ENABLED, and 503 rather than 404
// until their price ids are set — which is the honest distinction between "not
// for sale yet" and "misconfigured", and is why neither is published by this.
//
// Deliberately still a plain constant rather than an env var, for the same
// reason as PRICING_LINK_VISIBLE above: it's an editorial call that should be
// identical in every environment, so QA can never advertise a price prod
// doesn't. Kept rather than deleted so it stays a one-line way to take the
// prices back down.
export const SUBSCRIPTION_PRICING_VISIBLE = true;

/**
 * Whether this account may AUTHOR studio-layer content: scheduling, the public
 * booking page, invoicing and student-portal invites (I7).
 *
 * **Resolved by the server, never derived from the tier here.** It is true for
 * any paid tier, true for an org seat, true while the paywall is down, AND
 * true inside a dated grace window stamped on accounts that had these features
 * before they moved out of free — a window that deliberately does NOT raise
 * the tier, because the tier carrying the studio layer has no lesson AI and
 * pinning to it would take their recording away. Reimplementing that here
 * would get the grandfathered account wrong, which is the one account that
 * must not be.
 *
 * Absent reads as TRUE, like the kill-switch helpers above and for the same
 * reason: a payload that predates the field, or an older shipped build,
 * must not hide working affordances. The server 403 (`pro_required`) stays
 * authoritative — this only avoids offering an action that can only fail.
 *
 * @param {object} user - The instructor from /auth/me.
 * @returns {boolean}
 */
export function studioLayerEnabled(user) {
  return user?.studio_layer !== false;
}

/**
 * Which Calendar sync choices are switched on (Spec 19 §3.8), from any portal's
 * `me`: nested under `flags` for an instructor, a parent and an organization
 * (administrator or program viewer), flat on the student account.
 *
 * On the web `link` offers Apple Calendar and Other calendar app (both are the
 * private link) and `google` offers Google Calendar. Each is a launch switch
 * that defaults OFF, so an absent field reads as false: a payload that predates
 * it must not offer a choice the server would refuse with 404.
 *
 * @param {object} source - The portal's `me` (or the student account).
 * @returns {{link: boolean, google: boolean}}
 */
export function calendarSyncFlags(source) {
  const read = (key) => (source?.flags?.[key] ?? source?.[key]) === true;
  return { link: read("calendar_feeds_enabled"), google: read("google_calendar_sync_enabled") };
}
